import React, { useEffect, useState, useMemo } from "react";
import Cookies from "js-cookie";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUserInfo,
  changeEmailRequest,
  changePasswordRequest,
  clearEmailChangeError,
  clearPasswordChangeError,
  changePasswordFailure,
  clearEmailChangeSuccess,
  clearPasswordChangeSuccess,
  shopStatus
} from "../../store/actions";
import { fetchServicesRequest, fetchOrdersRequest } from "../../store/actions";
import moment from "moment";
import { connect } from "react-redux";
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Row,
  Table,
  Button,
  Modal,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  FormGroup,
  UncontrolledAlert,
  Spinner,
  Badge
} from "reactstrap";
import classnames from "classnames";
import Rating from "react-rating";
import { Link } from "react-router-dom";
import UserModal from "./UserModal";
import TableContainer from "components/Common/TableContainer";
import { Pdate, Ddate, Name, Idno, Budget } from "./AccCol";
import MapsGoogle from "pages/Maps/MapsGoogle";
import { toast } from "react-toastify";
import { toUpper } from "lodash";

const UserProfile = () => {
  document.title = "الحساب الشخصي | DBsaloni";
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [securityModalOpen, setSecurityModalOpen] = useState(false);
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("1");
  const [loading, setLoading] = useState(false);
  const [shopStatusLoading, setShopStatusLoading] = useState(false);

  // Selectors
  const {
    passwordChangeError,
    passwordChangeSucess,
    emailChangeError,
    emailChangeSucess,
    user,
    rates
  } = useSelector(state => state.Login);

  const totalCompletedOrders = useSelector(state => state.orders.totalCompletedOrders);
  const totalPendingOrders = useSelector(state => state.orders.totalPendingOrders);
  const services = useSelector(state => state.service.services);

  // Calculate average rating
  const averageRating = useMemo(() => {
    if (!rates.length) return 0;
    const totalRate = rates.reduce((sum, rate) => sum + rate.rating, 0);
    return totalRate / rates.length;
  }, [rates]);

  // Stats reports
  const reports = useMemo(() => [
    {
      title: "الطلبات المنجزة",
      iconClass: "bx-check-circle",
      description: totalCompletedOrders,
      color: "success"
    },
    {
      title: "الطلبات المعلقة",
      iconClass: "bx-hourglass",
      description: totalPendingOrders,
      color: "warning"
    },
    {
      title: "عدد التقييمات",
      iconClass: "bx-star",
      description: rates.length,
      color: "info"
    },
    {
      title: "متوسط التقييم",
      iconClass: "bx-award",
      description: averageRating.toFixed(1),
      color: "primary"
    }
  ], [totalCompletedOrders, totalPendingOrders, rates.length, averageRating]);

  // Fetch user data on mount
  useEffect(() => {
    const usersessionId = Cookies.get("userId", {
      secure: true,
      sameSite: "strict",
      path: "/",
    });
    if (usersessionId) {
      dispatch(fetchUserInfo(usersessionId));
    }
  }, [dispatch]);

  // Fetch services and orders
  useEffect(() => {
    dispatch(fetchServicesRequest());
    dispatch(fetchOrdersRequest());
  }, [dispatch]);

  // Tab handlers
  const toggleTab = tab => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  // Modal handlers
  const toggleSecurityModal = () => {
    setSecurityModalOpen(!securityModalOpen);
    setActiveTab("1");
    if (!securityModalOpen) {
      document.body.classList.add("no_padding");
    } else {
      document.body.classList.remove("no_padding");
    }
  };

  const openProfileModal = () => setIsModalOpen(true);
  const closeProfileModal = () => setIsModalOpen(false);

  // Handle update success
  const handleUpdateSuccess = () => {
    const usersessionId = Cookies.get("usersessionId", {
      secure: true,
      sameSite: "strict",
      path: "/",
    });
    if (usersessionId) {
      dispatch(fetchUserInfo(usersessionId));
    }
  };

  // Password change handler
  const handleSubmitPassword = async e => {
    e.preventDefault();
    const newPassword = e.target.newPassword.value;
    const confirmPassword = e.target.confirmPassword.value;

    if (newPassword !== confirmPassword) {
      dispatch(changePasswordFailure(new Error("كلمة المرور غير متطابقة")));
      return;
    }

    try {
      setLoading(true);
      dispatch(changePasswordRequest({ newPassword }));
      setIsModalOpen(false);
      toast.success("تم تحديث كلمة المرور بنجاح");
    } catch (error) {
      toast.error("حدث خطأ أثناء تحديث كلمة المرور");
    } finally {
      setLoading(false);
    }
  };

  // Email change handler
  const handleSubmitEmail = async e => {
    e.preventDefault();
    const newEmail = e.target.newEmail.value;
    const password = e.target.password.value;

    if (!isValidEmail(newEmail)) {
      dispatch(changeEmailFailure(new Error("بريد إلكتروني غير صالح")));
      return;
    }

    try {
      setLoading(true);
      await dispatch(changeEmailRequest({ newEmail, password }));
      setPasswordModalOpen(false);
      toast.success("تم تحديث البريد الإلكتروني بنجاح");
    } catch (error) {
      toast.error("حدث خطأ أثناء تحديث البريد الإلكتروني");
    } finally {
      setLoading(false);
    }
  };

  const isValidEmail = email => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Get account status badge color and text
  const getAccountStatusInfo = () => {
    if (!user?.account_status) return { color: "success", text: "نشط" };
    
    switch (user.account_status) {
      case "paused_for_subscription_expiry":
        return { color: "danger", text: "منتهي الاشتراك", icon: "bx-error" };
      case "action_required":
        return { color: "warning", text: "يتطلب إجراء", icon: "bx-exclamation" };
      case "active":
        return { color: "success", text: "نشط", icon: "bx-check" };
      default:
        return { color: "info", text: user.account_status, icon: "bx-info-circle" };
    }
  };

  // Get verification status for fields
  const getVerificationStatus = () => {
    return {
      email: !!user?.email,
      phone: !!user?.mobile,
      tax_number: !!user?.tax_number,
      commercial_register: !!user?.commercial_register,
      bank_account: !!user?.bank_account,
      description: !!user?.description
    };
  };

  // Recent activity data (sample - would come from backend)
  const recentActivities = useMemo(() => [
    { id: 1, action: "تحديث البيانات الشخصية", time: "منذ ساعة", icon: "bx-user-check" },
    { id: 2, action: "إنشاء طلب جديد", time: "منذ 3 ساعات", icon: "bx-plus-circle" },
    { id: 3, action: "حصول على تقييم 5 نجوم", time: "منذ يوم", icon: "bx-star" },
  ], []);

  // Shop status toggle
  const toggleShopStatus = async () => {
    if (!user) return;
    
    setShopStatusLoading(true);
    const newStatus = !user.is_online;
    
    toast.info(newStatus ? "جاري فتح المتجر..." : "جاري إغلاق المتجر...", {
      position: "top-center",
    });

    try {
      await dispatch(shopStatus(newStatus));
      toast.success(newStatus ? "تم فتح المتجر بنجاح" : "تم إغلاق المتجر بنجاح");
    } catch (error) {
      toast.error("حدث خطأ أثناء تغيير حالة المتجر");
    } finally {
      setShopStatusLoading(false);
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "$id",
        disableFilters: true,
        Cell: cellProps => <Idno {...cellProps} />,
      },
      {
        Header: "اسم الخدمة",
        accessor: "name",
        disableFilters: true,
        Cell: cellProps => <Name {...cellProps} />,
      },
      {
        Header: "نوع الخدمة",
        accessor: "service_type",
        disableFilters: true,
        Cell: cellProps => <Pdate {...cellProps} />,
      },
      {
        Header: "السعر",
        accessor: "service_price",
        disableFilters: true,
        Cell: cellProps => <Ddate {...cellProps} />,
      },
      {
        Header: "قسم الخدمة",
        accessor: "service_category",
        disableFilters: true,
        Cell: cellProps => <Budget {...cellProps} />,
      },
    ],
    []
  );

  return (
    <div className="page-content">
      <Container fluid>
        {/* Location Map */}
        <MapsGoogle />
        
        <Row>
          {/* Left Column - Profile Card */}
          <Col xl="4">
            <Card className="overflow-hidden profile-card">
              <div className="bg-primary bg-soft">
                <Row>
                  <Col
                    col="12"
                    style={{
                      backgroundImage: `url(${user?.profile_img_url || "./image/noimg.jpg"})`,
                      backgroundSize: "cover",
                      backgroundRepeat: "no-repeat",
                      width: "100%",
                      height: "35vh",
                      backgroundPosition: "center",
                    }}
                  >
                    <div className="text-primary p-3 text-white"></div>
                  </Col>
                </Row>
              </div>
              
              <CardBody className="pt-0">
                <Row>
                  <Col sm="4" className="profile-info">
                    <div className="avatar-md profile-user-wid mb-4"></div>
                    {user && (
                      <>
                        <h5 className="font-size-15 text-truncate">
                          {user.full_name}
                        </h5>
                        <small className="text-muted mb-0 text-truncate">
                          {user.email}
                        </small>
                        <small className="text-muted mb-0 text-truncate">
                          وقت العمل: {moment(user.opening_time, "HH:mm").format("hh:mm A")} - {moment(user.closing_time, "HH:mm").format("hh:mm A")}
                        </small>
                        <div className="mt-2">
                          <Badge color={user.is_online ? "success" : "warning"}>
                            {user.is_online ? "متصل" : "مغلق"}
                          </Badge>
                          <Badge color={getAccountStatusInfo().color} className="ms-2">
                            <i className={`bx ${getAccountStatusInfo().icon} me-1`}></i>
                            {getAccountStatusInfo().text}
                          </Badge>
                        </div>
                        <Button
                          onClick={toggleShopStatus}
                          color={user.is_online ? "success" : "warning"}
                          className="btn-sm mt-2"
                          disabled={shopStatusLoading}
                        >
                          {shopStatusLoading ? (
                            <>
                              <Spinner size="sm" /> جاري التحميل...
                            </>
                          ) : user.is_online ? (
                            "إغلاق المتجر"
                          ) : (
                            "فتح المتجر"
                          )}
                        </Button>
                      </>
                    )}
                  </Col>
                  
                  <Col sm="8">
                    <div className="pt-4">
                      <div className="p-4 text-center mt-4">
                        <h5 className="font-size-15">تقييم العملاء</h5>
                        <Rating
                          stop={5}
                          emptySymbol="mdi mdi-star-outline text-primary"
                          fullSymbol="mdi mdi-star text-primary"
                          initialRating={averageRating}
                          readonly
                          className="mb-2"
                        />
                        <small className="text-muted">
                          ({averageRating.toFixed(1)} من 5)
                        </small>

                        <div className="d-flex justify-content-center gap-2 mt-3">
                          <Button
                            onClick={openProfileModal}
                            color="primary"
                            className="btn-sm"
                          >
                            تعديل البيانات <i className="mdi mdi-account-edit ms-1"></i>
                          </Button>
                          
                          <Button
                            color="secondary"
                            className="btn-sm"
                            onClick={toggleSecurityModal}
                          >
                            الأمان <i className="mdi mdi-lock ms-1"></i>
                          </Button>
                        </div>

                        {isModalOpen && (
                          <UserModal
                            user={user}
                            closeModal={closeProfileModal}
                            handleUpdateSuccess={handleUpdateSuccess}
                          />
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
            
            {/* Salon Details Card */}
            {user && (
              <Card className="mt-4">
                <CardBody>
                  <CardTitle className="mb-4">
                    <h2>{toUpper(user.salon_name)}</h2>
                  </CardTitle>
                  <p className="text-muted mb-4">{user.description}</p>
                  <div className="table-responsive">
                    <Table className="table-nowrap mb-0">
                      <tbody>
                        <tr>
                          <th scope="row">الاسم الثلاثي:</th>
                          <td>{user.full_name}</td>
                        </tr>
                        <tr>
                          <th scope="row">رقم الهاتف:</th>
                          <td dir="ltr">{user.mobile}</td>
                        </tr>
                        <tr>
                          <th scope="row">البريد الإلكتروني:</th>
                          <td>{user.email}</td>
                        </tr>
                        <tr>
                          <th scope="row">عنوان الفرع:</th>
                          <td>{user.location}</td>
                        </tr>
                        <tr>
                          <th scope="row">الحساب البنكي:</th>
                          <td>{user.bank_account}</td>
                        </tr>
                        <tr>
                          <th scope="row">رقم الضريبة:</th>
                          <td>{user.tax_number}</td>
                        </tr>
                        <tr>
                          <th scope="row">العمولة:</th>
                          <td>{user.commission}%</td>
                        </tr>
                        <tr>
                          <th scope="row">السجل التجاري:</th>
                          <td>{user.commercial_register}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            )}
          </Col>

          {/* Right Column - Stats and Services */}
          <Col xl="8">
            {/* Stats Cards */}
            <Row>
              {reports.map((report, index) => (
                <Col md="6" key={`stat-${index}`}>
                  <Card className={`mini-stats-wid border border-${report.color}`}>
                    <CardBody>
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                          <p className="text-muted fw-medium mb-1">
                            {report.title}
                          </p>
                          <h4 className="mb-0">{report.description}</h4>
                        </div>
                        <div className="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
                          <span className={`avatar-title rounded-circle bg-${report.color}`}>
                            <i className={`bx ${report.iconClass} font-size-24`}></i>
                          </span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Working Hours Card */}
            {user && (
              <Card className="mt-4">
                <CardBody>
                  <CardTitle>
                    <i className="bx bx-time me-2"></i>
                    أوقات العمل
                  </CardTitle>
                  <Row>
                    <Col sm="6" className="text-center">
                      <div className="mb-3">
                        <p className="text-muted mb-1">وقت الافتتاح</p>
                        <h5 className="text-primary">
                          {moment(user.opening_time, "HH:mm").format("hh:mm A")}
                        </h5>
                      </div>
                    </Col>
                    <Col sm="6" className="text-center">
                      <div className="mb-3">
                        <p className="text-muted mb-1">وقت الإغلاق</p>
                        <h5 className="text-primary">
                          {moment(user.closing_time, "HH:mm").format("hh:mm A")}
                        </h5>
                      </div>
                    </Col>
                  </Row>
                  <div className="progress mt-3" style={{ height: "5px" }}>
                    <div
                      className="progress-bar bg-success"
                      role="progressbar"
                      style={{ width: "100%" }}
                    ></div>
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Verification Status Card */}
            {user && (
              <Card className="mt-4">
                <CardBody>
                  <CardTitle>
                    <i className="bx bx-check-circle me-2"></i>
                    حالة التحقق
                  </CardTitle>
                  <Row>
                    {Object.entries(getVerificationStatus()).map(([field, isVerified]) => {
                      const fieldLabels = {
                        email: "البريد الإلكتروني",
                        phone: "رقم الهاتف",
                        tax_number: "رقم الضريبة",
                        commercial_register: "السجل التجاري",
                        bank_account: "الحساب البنكي",
                        description: "الوصف"
                      };
                      
                      return (
                        <Col md="6" key={field} className="mb-2">
                          <div className="d-flex align-items-center">
                            <i 
                              className={`bx ${isVerified ? "bx-check-circle text-success" : "bx-x-circle text-danger"} me-2`}
                              style={{ fontSize: "18px" }}
                            ></i>
                            <span className={isVerified ? "text-success" : "text-danger"}>
                              {fieldLabels[field]}
                            </span>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </CardBody>
              </Card>
            )}

            {/* Recent Activity Card */}
            {/* <Card className="mt-4">
              <CardBody>
                <CardTitle>
                  <i className="bx bx-history me-2"></i>
                  النشاط الأخير
                </CardTitle>
                <div className="activity-timeline">
                  {recentActivities.map((activity, index) => (
                    <div key={activity.id} className="d-flex mb-3">
                      <div className="me-3">
                        <div 
                          className="avatar-sm rounded-circle bg-primary d-flex align-items-center justify-content-center"
                          style={{ width: "36px", height: "36px" }}
                        >
                          <i className={`bx ${activity.icon} text-white`}></i>
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="mb-1">{activity.action}</h6>
                        <small className="text-muted">{activity.time}</small>
                      </div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card> */}

            {/* Services Card */}
            <Card className="mt-4">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <CardTitle className="mb-0">خدماتي</CardTitle>
                  <Link to="/servicesManagment" className="btn btn-primary btn-sm">
                    إضافة خدمة جديدة <i className="mdi mdi-plus ms-1"></i>
                  </Link>
                </div>

                {services.length > 0 ? (
                  <TableContainer
                    columns={columns}
                    data={services}
                    isGlobalFilter={false}
                    customPageSize={5}
                    customPageSizeOptions={true}
                    className="mt-3"
                  />
                ) : (
                  <div className="text-center py-4">
                    <i className="bx bx-package font-size-24 text-muted mb-2"></i>
                    <p className="text-muted">لا توجد خدمات متاحة</p>
                    <Link to="/servicesManagment" className="btn btn-primary">
                      إضافة خدمة جديدة
                    </Link>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Security Modal (Password & Email Change) */}
        <Modal
          isOpen={securityModalOpen}
          toggle={toggleSecurityModal}
          centered
          className="modal-dialog-centered"
        >
          <div className="modal-header">
            <h5 className="modal-title">إعدادات الأمان</h5>
            <button
              type="button"
              onClick={toggleSecurityModal}
              className="close"
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <Nav tabs className="border-bottom">
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "1" })}
                onClick={() => setActiveTab("1")}
              >
                تغيير كلمة المرور
              </NavLink>
            </NavItem>
            {/* <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "2" })}
                onClick={() => setActiveTab("2")}
              >
                تغيير البريد الإلكتروني
              </NavLink>
            </NavItem> */}
          </Nav>

          <TabContent activeTab={activeTab} className="p-4">
            
            {/* Password Change Tab */}
            <TabPane tabId="1">
              <form onSubmit={handleSubmitPassword}>
                <FormGroup>
                  <label htmlFor="newPassword">كلمة المرور الجديدة:</label>
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    className="form-control mb-3"
                    placeholder="كلمة المرور الجديدة"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <label htmlFor="confirmPassword">تأكيد كلمة المرور:</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    className="form-control mb-3"
                    placeholder="تأكيد كلمة المرور"
                    required
                  />
                </FormGroup>
                <div className="d-flex justify-content-end">
                  <Button
                    type="button"
                    color="secondary"
                    onClick={toggleSecurityModal}
                    className="me-2"
                  >
                    إلغاء
                  </Button>
                  <Button type="submit" color="primary" disabled={loading}>
                    {loading ? (
                      <>
                        <Spinner size="sm" /> جاري الحفظ...
                      </>
                    ) : (
                      "حفظ التغييرات"
                    )}
                  </Button>
                </div>
              </form>
            </TabPane>

            {/* Email Change Tab */}
            <TabPane tabId="2">
              <form onSubmit={handleSubmitEmail}>
                <FormGroup>
                  <label htmlFor="currentEmail">البريد الإلكتروني الحالي:</label>
                  <input
                    type="email"
                    id="currentEmail"
                    className="form-control mb-3"
                    value={user?.email || ""}
                    disabled
                    placeholder="البريد الإلكتروني الحالي"
                  />
                </FormGroup>
                <FormGroup>
                  <label htmlFor="newEmail">البريد الإلكتروني الجديد:</label>
                  <input
                    type="email"
                    id="newEmail"
                    name="newEmail"
                    className="form-control mb-3"
                    placeholder="أدخل البريد الإلكتروني الجديد"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <label htmlFor="emailPassword">كلمة المرور:</label>
                  <input
                    type="password"
                    id="emailPassword"
                    name="password"
                    className="form-control mb-3"
                    placeholder="أدخل كلمة المرور للتأكيد"
                    required
                  />
                </FormGroup>
                <div className="d-flex justify-content-end">
                  <Button
                    type="button"
                    color="secondary"
                    onClick={toggleSecurityModal}
                    className="me-2"
                  >
                    إلغاء
                  </Button>
                  <Button type="submit" color="primary" disabled={loading}>
                    {loading ? (
                      <>
                        <Spinner size="sm" /> جاري الحفظ...
                      </>
                    ) : (
                      "حفظ التغييرات"
                    )}
                  </Button>
                </div>
              </form>
            </TabPane>
          </TabContent>
        </Modal>
      </Container>
    </div>
  );
};

const mapStateToProps = state => ({
  orders: state.orders.orders,
  user: state.Login.user,
});

export default connect(mapStateToProps)(UserProfile);